import React, { useState } from 'react';
import styled from 'styled-components';
import { Badge } from './Badge';

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  hideOnMobile?: boolean;
  isPrimary?: boolean; // Primary field shown as card title on mobile
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  selectedIds?: string[];
  onSelect?: (ids: string[]) => void;
  emptyMessage?: string;
  isLoading?: boolean;
  /** data-testid for the table wrapper element. */
  testId?: string;
  /** Returns a data-testid for an individual row. Receives the row item. */
  getRowTestId?: (item: T) => string;
  /** Static data-flow value applied to every row element. */
  rowDataFlow?: string;
}

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile Card Grid View
const MobileCardGrid = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const MobileCard = styled.div<{ $clickable?: boolean; $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  position: relative;
  
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
  `}
  
  ${({ $selected, theme }) => $selected && `
    border-color: ${theme.colors.primary};
    border-left: 4px solid ${theme.colors.primary};
    background: ${theme.colors.primaryLight};
  `}
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.borderDark};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
  
  &:active {
    transform: scale(0.99);
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const MobileCardTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const MobileCardCheckbox = styled.div`
  flex-shrink: 0;
`;

const MobileCardBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  
  &:not(:last-child) {
    border-bottom: 1px dashed ${({ theme }) => theme.colors.borderLight};
  }
`;

const MobileCardLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  flex-shrink: 0;
  min-width: 80px;
`;

const MobileCardValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  text-align: right;
  flex: 1;
  word-break: break-word;
`;

const MobileEmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const MobileLoadingState = styled(MobileEmptyState)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
`;

const Th = styled.th<{ $sortable?: boolean; $width?: string }>`
  padding: 14px ${({ theme }) => theme.spacing.lg};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  width: ${({ $width }) => $width || 'auto'};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${({ $sortable }) => $sortable && `
    cursor: pointer;
    user-select: none;
    
    &:hover {
      color: ${({ theme }: { theme: unknown }) => (theme as { colors: { text: string } }).colors.text};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr<{ $clickable?: boolean; $selected?: boolean }>`
  transition: all ${({ theme }) => theme.transitions.fast};
  
  ${({ $clickable }) => $clickable && `
    cursor: pointer;
  `}
  
  ${({ $selected, theme }) => $selected && `
    background: ${theme.colors.primaryLight};
    border-left: 3px solid ${theme.colors.primary};
  `}
  
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Td = styled.td`
  padding: 14px ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  
  ${Tr}:last-child & {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing['2xl']};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const LoadingRow = styled.tr`
  td {
    padding: ${({ theme }) => theme.spacing.xl};
    text-align: center;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

export function Table<T extends { id: string }>({
  data,
  columns,
  onRowClick,
  selectedIds = [],
  onSelect,
  emptyMessage = 'No data available',
  isLoading = false,
  testId,
  getRowTestId,
  rowDataFlow,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (!onSelect) return;
    if (selectedIds.length === data.length) {
      onSelect([]);
    } else {
      onSelect(data.map(item => item.id));
    }
  };

  const handleSelectOne = (id: string) => {
    if (!onSelect) return;
    if (selectedIds.includes(id)) {
      onSelect(selectedIds.filter(i => i !== id));
    } else {
      onSelect([...selectedIds, id]);
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = (a as Record<string, unknown>)[sortKey];
    const bVal = (b as Record<string, unknown>)[sortKey];

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  // Find primary column for mobile card title (first column or one marked isPrimary)
  const primaryColumn = columns.find(col => col.isPrimary) || columns[0];
  // Filter columns for mobile display (exclude hidden and primary)
  const mobileColumns = columns.filter(col => !col.hideOnMobile && col.key !== primaryColumn?.key);

  // Mobile Card View
  const renderMobileView = () => {
    if (isLoading) {
      return (
        <MobileLoadingState>
          <span>Loading...</span>
        </MobileLoadingState>
      );
    }

    if (sortedData.length === 0) {
      return <MobileEmptyState>{emptyMessage}</MobileEmptyState>;
    }

    return (
      <MobileCardGrid>
        {sortedData.map(item => (
          <MobileCard
            key={item.id}
            $clickable={!!onRowClick}
            $selected={selectedIds.includes(item.id)}
            onClick={() => onRowClick?.(item)}
          >
            <MobileCardHeader>
              <MobileCardTitle>
                {primaryColumn?.render
                  ? primaryColumn.render(item)
                  : String((item as Record<string, unknown>)[primaryColumn?.key || ''] ?? '')}
              </MobileCardTitle>
              {onSelect && (
                <MobileCardCheckbox onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedIds.includes(item.id)}
                    onChange={() => handleSelectOne(item.id)}
                  />
                </MobileCardCheckbox>
              )}
            </MobileCardHeader>
            <MobileCardBody>
              {mobileColumns.map(col => (
                <MobileCardRow key={col.key}>
                  <MobileCardLabel>{col.header}</MobileCardLabel>
                  <MobileCardValue>
                    {col.render
                      ? col.render(item)
                      : String((item as Record<string, unknown>)[col.key] ?? '')}
                  </MobileCardValue>
                </MobileCardRow>
              ))}
            </MobileCardBody>
          </MobileCard>
        ))}
      </MobileCardGrid>
    );
  };

  return (
    <>
      {/* Desktop Table View */}
      <TableWrapper data-testid={testId}>
        <StyledTable>
          <Thead>
            <tr>
              {onSelect && (
                <Th $width="40px">
                  <Checkbox
                    type="checkbox"
                    checked={selectedIds.length === data.length && data.length > 0}
                    onChange={handleSelectAll}
                  />
                </Th>
              )}
              {columns.map(col => (
                <Th
                  key={col.key}
                  $sortable={col.sortable}
                  $width={col.width}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.header}
                  {sortKey === col.key && (
                    <span style={{ marginLeft: 4 }}>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </Th>
              ))}
            </tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <LoadingRow>
                <td colSpan={columns.length + (onSelect ? 1 : 0)}>
                  Loading...
                </td>
              </LoadingRow>
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (onSelect ? 1 : 0)}>
                  <EmptyState>{emptyMessage}</EmptyState>
                </td>
              </tr>
            ) : (
              sortedData.map(item => (
                <Tr
                  key={item.id}
                  $clickable={!!onRowClick}
                  $selected={selectedIds.includes(item.id)}
                  onClick={() => onRowClick?.(item)}
                  data-testid={getRowTestId ? getRowTestId(item) : undefined}
                  data-flow={rowDataFlow}
                >
                  {onSelect && (
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleSelectOne(item.id)}
                      />
                    </Td>
                  )}
                  {columns.map(col => (
                    <Td key={col.key}>
                      {col.render
                        ? col.render(item)
                        : String((item as Record<string, unknown>)[col.key] ?? '')}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </StyledTable>
      </TableWrapper>

      {/* Mobile Card View */}
      {renderMobileView()}
    </>
  );
}

export { Badge };

